import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { Loader2, Search, TrendingDown, TrendingUp, Euro, Calendar, Building2, ChevronDown, ChevronRight, FolderTree, Maximize2, Minimize2, Layers, ArrowLeftRight, Info, Clock } from "lucide-react";
import { api } from "../App";
import { getApiPrefix } from "../utils/apiHelper";
import { useTranslation } from "../contexts/I18nContext";
import { LayoutEditorDialog, VistaMargini, useActiveLayout } from "../components/BilancioMargini";
import { aggregateByMesi, aggregateByCDC, aggregateCdcRiepilogo } from "../utils/bilancioAggregation";
import { CdcRiepilogoCard } from "../components/bilancio/CdcRiepilogoCard";
import DrilldownDialog from "../components/bilancio/DrilldownDialog";

export default function BilancioCompetenzaPage() {
  const { t } = useTranslation();
  const MESI = [
    { value: '01', label: t('months.january'), full: 'Gennaio' },
    { value: '02', label: t('months.february'), full: 'Febbraio' },
    { value: '03', label: t('months.march'), full: 'Marzo' },
    { value: '04', label: t('months.april'), full: 'Aprile' },
    { value: '05', label: t('months.may'), full: 'Maggio' },
    { value: '06', label: t('months.june'), full: 'Giugno' },
    { value: '07', label: t('months.july'), full: 'Luglio' },
    { value: '08', label: t('months.august'), full: 'Agosto' },
    { value: '09', label: t('months.september'), full: 'Settembre' },
    { value: '10', label: t('months.october'), full: 'Ottobre' },
    { value: '11', label: t('months.november'), full: 'Novembre' },
    { value: '12', label: t('months.december'), full: 'Dicembre' },
  ];
  const currentYear = new Date().getFullYear();
  const [loading, setLoading] = useState(false);
  const [anno, setAnno] = useState(String(currentYear));
  
  // Modalità colonne: "mesi" = colonne sono i mesi, "cdc" = colonne sono i centri di costo
  const [columnMode, setColumnMode] = useState("mesi");
  // Mostra costo orario €/h sotto ogni importo (richiede ore manodopera valorizzate)
  const [showCostoOrario, setShowCostoOrario] = useState(false);
  
  // Selezione mesi (usato sia come colonne che come filtro)
  const [selectedMesi, setSelectedMesi] = useState(["01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12"]);
  
  // Centri di costo disponibili e selezionati
  const [costCenters, setCostCenters] = useState([]);
  const [selectedCentriCosto, setSelectedCentriCosto] = useState([]);
  
  const [reportData, setReportData] = useState(null);
  const [manodoperaAggregata, setManodoperaAggregata] = useState({ totale_ore: 0, per_mese: {}, per_cdc: {}, per_mese_cdc: {} });
  const [layoutEditorOpen, setLayoutEditorOpen] = useState(false);
  const [vistaScalare, setVistaScalare] = useState(false);
  const [contiDisponibili, setContiDisponibili] = useState([]);
  const [categorieDisponibili, setCategorieDisponibili] = useState([]);
  const [cdcRiepilogoData, setCdcRiepilogoData] = useState(null);
  const [costiViewMode, setCostiViewMode] = useState("ripartiti");
  const [expandedContiRows, setExpandedContiRows] = useState(new Set());
  const { activeLayout, fetchActiveLayout } = useActiveLayout();
  const [drilldown, setDrilldown] = useState({ open: false, entries: [], contoNome: "", contoCodice: "", columnLabel: "" });

  // Carica centri di costo
  useEffect(() => {
    const fetchCostCenters = async () => {
      try {
        const prefix = getApiPrefix();
        const res = await api.get(`${prefix}/cost-centers`);
        const cc = res.data || [];
        setCostCenters(cc);
        // Seleziona tutti i centri di costo di default
        setSelectedCentriCosto(cc.map(c => c.id));
      } catch (err) {
        
      }
    };
    fetchCostCenters();
  }, []);

  // Carica conti disponibili per l'editor layout margini
  useEffect(() => {
    const fetchConti = async () => {
      try {
        const prefix = getApiPrefix();
        const res = await api.get(`${prefix}/accounts`);
        setContiDisponibili(Array.isArray(res.data) ? res.data : []);
      } catch { /* ignore */ }
    };
    const fetchCategorie = async () => {
      try {
        const prefix = getApiPrefix();
        const res = await api.get(`${prefix}/account-categories`);
        setCategorieDisponibili(Array.isArray(res.data) ? res.data : []);
      } catch { /* ignore */ }
    };
    fetchConti();
    fetchCategorie();
  }, []);

  const formatCurrency = (val) => {
    if (val === null || val === undefined) return "-";
    return new Intl.NumberFormat("it-IT", { 
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(val || 0);
  };

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const prefix = getApiPrefix();
      const results = {};
      const manodoperaPerMeseCdc = {}; // accumulato durante il fetch, indipendente dal flow
      const collectMd = (md) => {
        if (!md || !md.per_mese_cdc) return;
        Object.entries(md.per_mese_cdc).forEach(([m, byCdc]) => {
          manodoperaPerMeseCdc[m] = manodoperaPerMeseCdc[m] || {};
          Object.entries(byCdc).forEach(([cc, ore]) => {
            manodoperaPerMeseCdc[m][cc] = ore; // overwrite: valore univoco per (mese, cdc)
          });
        });
      };
      
      if (columnMode === "mesi") {
        // Colonne = Mesi, per ogni mese carica i dati (filtrati per CDC selezionati se necessario)
        if (selectedCentriCosto.length === costCenters.length) {
          // Tutti i CDC: chiamate parallele senza filtro CDC
          const promises = selectedMesi.map(async (mese) => {
            const params = new URLSearchParams({ anno, mese });
            const res = await api.get(`${prefix}/reports/bilancio-competenza?${params.toString()}`);
            return { mese, data: res.data };
          });
          const responses = await Promise.all(promises);
          for (const { mese, data } of responses) {
            collectMd(data?.manodopera);
            results[mese] = { "__all__": data };
          }
        } else {
          // CDC specifici: chiamate parallele per ogni combinazione mese+CDC
          // I costi senza CDC vengono inclusi SOLO nella prima chiamata di ogni mese
          const promises = [];
          for (const mese of selectedMesi) {
            selectedCentriCosto.forEach((cdcId, idx) => {
              // Includi costi senza CDC solo nella prima chiamata per evitare doppi conteggi
              const includiSenzaCdc = idx === 0;
              promises.push(
                api.get(`${prefix}/reports/bilancio-competenza?anno=${anno}&mese=${mese}&centro_costo_id=${cdcId}&includi_senza_cdc=${includiSenzaCdc}`)
                  .then(res => ({ mese, cdcId, data: res.data, hasSenzaCdc: includiSenzaCdc }))
              );
            });
          }
          const responses = await Promise.all(promises);
          for (const { mese, cdcId, data } of responses) {
            collectMd(data?.manodopera);
            if (!results[mese]) results[mese] = {};
            results[mese][cdcId] = data;
          }
        }
      } else {
        // Colonne = Centri di Costo
        // Ottimizzazione: facciamo una singola chiamata per mese senza filtro CDC
        // e poi filtriamo lato client per CDC
        const promises = [];
        
        for (const mese of selectedMesi) {
          promises.push(
            api.get(`${prefix}/reports/bilancio-competenza?anno=${anno}&mese=${mese}`)
              .then(res => ({ mese, data: res.data }))
          );
        }
        
        const responses = await Promise.all(promises);
        
        // Ora distribuisci i dati per CDC
        for (const { mese, data } of responses) {
          collectMd(data?.manodopera);
          // Pre-calcola lookup per_conto (usato anche per "Senza CDC")
          const apiCostiPerConto = {};
          (data.costi?.per_conto || []).forEach(pc => {
            if (pc.conto_id) apiCostiPerConto[pc.conto_id] = pc;
          });
          const apiRicaviPerConto = {};
          (data.ricavi?.per_conto || []).forEach(pc => {
            if (pc.conto_id) apiRicaviPerConto[pc.conto_id] = pc;
          });
          const apiCostiOrigPerConto = {};
          (data.costi_originali?.per_conto || []).forEach(pc => {
            if (pc.conto_id) apiCostiOrigPerConto[pc.conto_id] = pc;
          });

          // Per ogni CDC, filtra i costi/ricavi che appartengono a quel CDC
          for (const cdcId of selectedCentriCosto) {
            if (!results[cdcId]) results[cdcId] = {};
            
            const cdcCosti = data.costi?.dettaglio?.filter(c => c.centro_costo_id === cdcId) || [];
            const cdcRicavi = data.ricavi?.dettaglio?.filter(r => r.centro_costo_id === cdcId) || [];
            const cdcCostiOrig = data.costi_originali?.dettaglio?.filter(c => c.centro_costo_id === cdcId) || [];
            
            const totaleCosti = cdcCosti.reduce((sum, c) => sum + (c.importo || 0), 0);
            const totaleRicavi = cdcRicavi.reduce((sum, r) => sum + (r.importo || 0), 0);
            const totaleCostiOrig = cdcCostiOrig.reduce((sum, c) => sum + (c.importo || 0), 0);
            
            // Aggrega per conto - con info categoria dal per_conto originale
            const costiPerConto = {};
            for (const c of cdcCosti) {
              const key = c.conto_nome || "Senza conto";
              if (!costiPerConto[key]) {
                const catInfo = apiCostiPerConto[c.conto_id] || {};
                costiPerConto[key] = { 
                  conto_nome: c.conto_nome, conto_codice: c.conto_codice, 
                  conto_id: c.conto_id,
                  categoria_id: catInfo.categoria_id,
                  categoria_nome: catInfo.categoria_nome,
                  categoria_codice: catInfo.categoria_codice,
                  categoria_ordine: catInfo.categoria_ordine,
                  totale: 0,
                  dettaglio: []
                };
              }
              costiPerConto[key].totale += c.importo || 0;
              costiPerConto[key].dettaglio.push(c);
            }
            
            const costiOrigPerConto = {};
            for (const c of cdcCostiOrig) {
              const key = c.conto_nome || "Senza conto";
              if (!costiOrigPerConto[key]) {
                const catInfo = apiCostiOrigPerConto[c.conto_id] || apiCostiPerConto[c.conto_id] || {};
                costiOrigPerConto[key] = { 
                  conto_nome: c.conto_nome, conto_codice: c.conto_codice, 
                  conto_id: c.conto_id,
                  categoria_id: catInfo.categoria_id,
                  categoria_nome: catInfo.categoria_nome,
                  categoria_codice: catInfo.categoria_codice,
                  categoria_ordine: catInfo.categoria_ordine,
                  totale: 0,
                  dettaglio: []
                };
              }
              costiOrigPerConto[key].totale += c.importo || 0;
              costiOrigPerConto[key].dettaglio.push(c);
            }
            
            const ricaviPerConto = {};
            for (const r of cdcRicavi) {
              const key = r.conto_nome || "Senza conto";
              if (!ricaviPerConto[key]) {
                const catInfo = apiRicaviPerConto[r.conto_id] || {};
                ricaviPerConto[key] = { 
                  conto_nome: r.conto_nome, conto_codice: r.conto_codice,
                  conto_id: r.conto_id,
                  categoria_id: catInfo.categoria_id,
                  categoria_nome: catInfo.categoria_nome,
                  categoria_codice: catInfo.categoria_codice,
                  categoria_ordine: catInfo.categoria_ordine,
                  totale: 0,
                  dettaglio: []
                };
              }
              ricaviPerConto[key].totale += r.importo || 0;
              ricaviPerConto[key].dettaglio.push(r);
            }
            
            results[cdcId][mese] = {
              totale_costi: totaleCosti,
              totale_ricavi: totaleRicavi,
              risultato: totaleRicavi - totaleCosti,
              costi: { per_conto: Object.values(costiPerConto), dettaglio: cdcCosti },
              ricavi: { per_conto: Object.values(ricaviPerConto), dettaglio: cdcRicavi },
              costi_originali: { per_conto: Object.values(costiOrigPerConto), totale: totaleCostiOrig }
            };
          }
          
          // Aggiungi anche "Senza CDC"
          const senzaCdcCosti = data.costi?.dettaglio?.filter(c => !c.centro_costo_id) || [];
          const senzaCdcRicavi = data.ricavi?.dettaglio?.filter(r => !r.centro_costo_id) || [];
          const senzaCdcCostiOrig = data.costi_originali?.dettaglio?.filter(c => !c.centro_costo_id) || [];
          
          if (senzaCdcCosti.length > 0 || senzaCdcRicavi.length > 0 || senzaCdcCostiOrig.length > 0) {
            if (!results["__senza_cdc__"]) results["__senza_cdc__"] = {};
            
            const totaleCosti = senzaCdcCosti.reduce((sum, c) => sum + (c.importo || 0), 0);
            const totaleRicavi = senzaCdcRicavi.reduce((sum, r) => sum + (r.importo || 0), 0);
            const totaleCostiOrig = senzaCdcCostiOrig.reduce((sum, c) => sum + (c.importo || 0), 0);
            
            const costiPerContoSenza = {};
            for (const c of senzaCdcCosti) {
              const key = c.conto_nome || "Senza conto";
              if (!costiPerContoSenza[key]) {
                const catInfo = apiCostiPerConto[c.conto_id] || {};
                costiPerContoSenza[key] = { 
                  conto_nome: c.conto_nome, conto_codice: c.conto_codice,
                  conto_id: c.conto_id,
                  categoria_id: catInfo.categoria_id,
                  categoria_nome: catInfo.categoria_nome,
                  categoria_codice: catInfo.categoria_codice,
                  categoria_ordine: catInfo.categoria_ordine,
                  totale: 0,
                  dettaglio: []
                };
              }
              costiPerContoSenza[key].totale += c.importo || 0;
              costiPerContoSenza[key].dettaglio.push(c);
            }
            
            const costiOrigPerContoSenza = {};
            for (const c of senzaCdcCostiOrig) {
              const key = c.conto_nome || "Senza conto";
              if (!costiOrigPerContoSenza[key]) {
                const catInfo = apiCostiOrigPerConto?.[c.conto_id] || apiCostiPerConto[c.conto_id] || {};
                costiOrigPerContoSenza[key] = { 
                  conto_nome: c.conto_nome, conto_codice: c.conto_codice,
                  conto_id: c.conto_id,
                  categoria_id: catInfo.categoria_id,
                  categoria_nome: catInfo.categoria_nome,
                  categoria_codice: catInfo.categoria_codice,
                  categoria_ordine: catInfo.categoria_ordine,
                  totale: 0,
                  dettaglio: []
                };
              }
              costiOrigPerContoSenza[key].totale += c.importo || 0;
              costiOrigPerContoSenza[key].dettaglio.push(c);
            }
            
            const ricaviPerContoSenza = {};
            for (const r of senzaCdcRicavi) {
              const key = r.conto_nome || "Senza conto";
              if (!ricaviPerContoSenza[key]) {
                const catInfo = apiRicaviPerConto[r.conto_id] || {};
                ricaviPerContoSenza[key] = { 
                  conto_nome: r.conto_nome, conto_codice: r.conto_codice,
                  conto_id: r.conto_id,
                  categoria_id: catInfo.categoria_id,
                  categoria_nome: catInfo.categoria_nome,
                  categoria_codice: catInfo.categoria_codice,
                  categoria_ordine: catInfo.categoria_ordine,
                  totale: 0,
                  dettaglio: []
                };
              }
              ricaviPerContoSenza[key].totale += r.importo || 0;
              ricaviPerContoSenza[key].dettaglio.push(r);
            }
            
            results["__senza_cdc__"][mese] = {
              totale_costi: totaleCosti,
              totale_ricavi: totaleRicavi,
              risultato: totaleRicavi - totaleCosti,
              costi: { per_conto: Object.values(costiPerContoSenza), dettaglio: senzaCdcCosti },
              ricavi: { per_conto: Object.values(ricaviPerContoSenza), dettaglio: senzaCdcRicavi },
              costi_originali: { per_conto: Object.values(costiOrigPerContoSenza), totale: totaleCostiOrig }
            };
          }
        }
      }
      
      setReportData(results);

      // Calcola aggregati derivati da manodoperaPerMeseCdc raccolto durante il fetch
      const perMese = {};
      const perCdc = {};
      let totaleOre = 0;
      Object.entries(manodoperaPerMeseCdc).forEach(([m, byCdc]) => {
        let monthTot = 0;
        Object.entries(byCdc).forEach(([cc, ore]) => {
          perCdc[cc] = (perCdc[cc] || 0) + ore;
          monthTot += ore;
        });
        perMese[m] = monthTot;
        totaleOre += monthTot;
      });
      setManodoperaAggregata({
        totale_ore: totaleOre,
        per_mese: perMese,
        per_cdc: perCdc,
        per_mese_cdc: manodoperaPerMeseCdc,
      });

      // Aggrega dati CDC ripartizione da tutte le risposte mensili
      if (columnMode === "mesi") {
        setCdcRiepilogoData(aggregateCdcRiepilogo(results, selectedMesi));
      } else {
        setCdcRiepilogoData(null);
      }
      showSuccess(t("success.reportGenerated"));
      // Scroll al report dopo la generazione
      setTimeout(() => {
        const reportTable = document.querySelector('[data-testid="bilancio-report-table"]');
        if (reportTable) {
          reportTable.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
    } catch (err) {
      handleApiError(err, t("errors.errorGenReport"));
      setReportData(null);
    } finally {
      setLoading(false);
    }
  }, [anno, selectedMesi, selectedCentriCosto, columnMode, costCenters.length]);

  // Ref per il debounce timer
  const debounceRef = useRef(null);

  // Auto-refresh del report quando cambiano i filtri (con debounce per permettere selezioni multiple)
  useEffect(() => {
    // Cancella il timer precedente se esiste
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    // Se c'è già un report generato, aspetta 800ms prima di rigenerare
    if (reportData && !loading && selectedMesi.length > 0 && selectedCentriCosto.length > 0) {
      debounceRef.current = setTimeout(() => {
        fetchReport();
      }, 800);
    }
    
    // Cleanup al dismount
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMesi, selectedCentriCosto, columnMode]);

  const anni = [];
  for (let y = currentYear + 1; y >= currentYear - 5; y--) {
    anni.push(String(y));
  }

  // Aggrega dati per visualizzazione tabellare - delegato a utility
  const aggregatedData = columnMode === "mesi" 
    ? aggregateByMesi(reportData, selectedMesi) 
    : aggregateByCDC(reportData, selectedMesi, selectedCentriCosto);
    
  const { costi, ricavi, totali, costiPerCategoria, ricaviPerCategoria, costiOriginaliPerCategoria, totaliOriginali, ripartizioneInfo, dettaglioMap, dettaglioMapOriginali } = aggregatedData;
  
  // Mappa dettaglio attiva in base alla vista costi (ripartiti vs originali).
  // In vista "originali" merge dettaglioMapOriginali (solo costi) sopra dettaglioMap (per preservare ricavi e drill-down su entrambi).
  const dettaglioMapAttivo = useMemo(() => {
    if (columnMode === "cdc" && costiViewMode === "originali" && dettaglioMapOriginali && Object.keys(dettaglioMapOriginali).length > 0) {
      return { ...(dettaglioMap || {}), ...dettaglioMapOriginali };
    }
    return dettaglioMap || {};
  }, [columnMode, costiViewMode, dettaglioMap, dettaglioMapOriginali]);
  
  // Helper per ore manodopera per colonna (mese o cdc)
  const manodoperaData = manodoperaAggregata;
  const oreTotaliPeriodo = useMemo(() => {
    if (columnMode === "mesi") {
      return selectedMesi.reduce((sum, m) => {
        const k = `${anno}-${m}`;
        return sum + (manodoperaData.per_mese?.[k] || 0);
      }, 0);
    }
    return selectedCentriCosto.reduce((sum, ccid) => sum + (manodoperaData.per_cdc?.[ccid] || 0), 0);
  }, [manodoperaData, selectedMesi, selectedCentriCosto, columnMode, anno]);

  const getOreColonna = (colKey) => {
    if (columnMode === "mesi") {
      return manodoperaData.per_mese?.[`${anno}-${colKey}`] || 0;
    }
    return manodoperaData.per_cdc?.[colKey] || 0;
  };

  const formatEuroOra = (importo, ore) => {
    if (!ore || ore <= 0) return null;
    const v = importo / ore;
    return `${v.toFixed(2)} €/h`;
  };
  
  // Determina quale set di dati costi usare in base alla vista
  const costiCategorieAttive = costiViewMode === "originali" && costiOriginaliPerCategoria && Object.keys(costiOriginaliPerCategoria).length > 0
    ? costiOriginaliPerCategoria
    : costiPerCategoria;
  const totaliCostiAttivi = costiViewMode === "originali" && totaliOriginali
    ? totaliOriginali
    : totali;
  
  // State per categorie espanse/collassate
  const [expandedCostiCategories, setExpandedCostiCategories] = useState(new Set());
  const [expandedRicaviCategories, setExpandedRicaviCategories] = useState(new Set());
  
  const toggleCostiCategory = (catId) => {
    const newSet = new Set(expandedCostiCategories);
    if (newSet.has(catId)) {
      newSet.delete(catId);
    } else {
      newSet.add(catId);
    }
    setExpandedCostiCategories(newSet);
  };
  
  const toggleRicaviCategory = (catId) => {
    const newSet = new Set(expandedRicaviCategories);
    if (newSet.has(catId)) {
      newSet.delete(catId);
    } else {
      newSet.add(catId);
    }
    setExpandedRicaviCategories(newSet);
  };
  
  const expandAllCosti = () => setExpandedCostiCategories(new Set(Object.keys(costiPerCategoria || {})));
  const collapseAllCosti = () => setExpandedCostiCategories(new Set());
  const expandAllRicavi = () => setExpandedRicaviCategories(new Set(Object.keys(ricaviPerCategoria || {})));
  const collapseAllRicavi = () => setExpandedRicaviCategories(new Set());

  const toggleContoRipartizione = (contoKey) => {
    const newSet = new Set(expandedContiRows);
    if (newSet.has(contoKey)) {
      newSet.delete(contoKey);
    } else {
      newSet.add(contoKey);
    }
    setExpandedContiRows(newSet);
  };

  // Toggle mese
  const toggleMese = (mese) => {
    if (selectedMesi.includes(mese)) {
      if (selectedMesi.length > 1) {
        setSelectedMesi(selectedMesi.filter(m => m !== mese));
      }
    } else {
      setSelectedMesi([...selectedMesi, mese].sort((a, b) => parseInt(a) - parseInt(b)));
    }
  };

  // Toggle centro di costo
  const toggleCentroCosto = (cdcId) => {
    if (selectedCentriCosto.includes(cdcId)) {
      if (selectedCentriCosto.length > 1) {
        setSelectedCentriCosto(selectedCentriCosto.filter(c => c !== cdcId));
      }
    } else {
      setSelectedCentriCosto([...selectedCentriCosto, cdcId]);
    }
  };

  const selectAllMesi = () => setSelectedMesi(MESI.map(m => m.value));
  const selectQ1 = () => setSelectedMesi(["01", "02", "03"]);
  const selectQ2 = () => setSelectedMesi(["04", "05", "06"]);
  const selectQ3 = () => setSelectedMesi(["07", "08", "09"]);
  const selectQ4 = () => setSelectedMesi(["10", "11", "12"]);
  const selectH1 = () => setSelectedMesi(["01", "02", "03", "04", "05", "06"]);
  const selectH2 = () => setSelectedMesi(["07", "08", "09", "10", "11", "12"]);

  const selectAllCDC = () => setSelectedCentriCosto(costCenters.map(c => c.id));

  // Calcola totale riga
  const getRowTotal = (valuesData, keys) => {
    return keys.reduce((sum, key) => sum + (valuesData[key] || 0), 0);
  };

  // Drill-down: apri dettaglio di una cella.
  const openDrilldown = (contoNome, contoCodice, columnKey) => {
    const entries = (dettaglioMapAttivo || {})[contoNome]?.[columnKey] || [];
    if (entries.length === 0) return;
    const col = columns.find(c => c.key === columnKey);
    setDrilldown({ open: true, entries, contoNome, contoCodice, columnLabel: col?.label || columnKey });
  };

  // Ottieni le colonne correnti in base alla modalità
  const getColumns = () => {
    if (columnMode === "mesi") {
      return selectedMesi.map(m => ({
        key: m,
        label: MESI.find(mese => mese.value === m)?.full || m,
        shortLabel: MESI.find(mese => mese.value === m)?.label || m
      }));
    } else {
      const cols = selectedCentriCosto.map(cdcId => {
        const cdc = costCenters.find(c => c.id === cdcId);
        return {
          key: cdcId,
          label: cdc ? `${cdc.codice} - ${cdc.nome}` : cdcId,
          shortLabel: cdc?.codice || cdcId
        };
      });
      // Aggiungi colonna "Senza CDC" se ci sono dati
      if (reportData && reportData["__senza_cdc__"]) {
        cols.push({
          key: "__senza_cdc__",
          label: "Senza CDC",
          shortLabel: "N/A"
        });
      }
      return cols;
    }
  };

  const columns = getColumns();
  const columnKeys = columns.map(c => c.key);

  return (
    <div className="space-y-6" data-testid="bilancio-competenza-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("reports.accrualBalance")}</h1>
          <p className="text-muted-foreground text-sm">Analisi costi e ricavi per periodo</p>
        </div>
        <Badge variant="outline" className="text-sm px-3 py-1 font-mono">
          Anno {anno}
        </Badge>
      </div>

      {/* Toggle Vista + Configura Margini */}
      <div className="flex items-center gap-2">
        <div className="flex gap-1 p-1 bg-muted rounded-lg">
          <Button variant={!vistaScalare ? "default" : "ghost"} size="sm" onClick={() => setVistaScalare(false)}>
            Vista Standard
          </Button>
          <Button 
            variant={vistaScalare ? "default" : "ghost"} 
            size="sm" 
            onClick={() => activeLayout && setVistaScalare(true)} 
            className={`gap-1.5 ${!activeLayout ? "opacity-50 cursor-not-allowed" : ""}`}
            disabled={!activeLayout}
            data-testid="toggle-vista-scalare"
          >
            <Layers className="h-4 w-4" /> {activeLayout ? activeLayout.nome : "CE a Scalare"}
          </Button>
        </div>
        {!activeLayout && (
          <span className="text-xs text-muted-foreground">Configura un layout Margini per abilitare la vista CE a Scalare</span>
        )}
      </div>

      {/* Filtri - Improved Design */}
      <Card className="shadow-sm">
        <CardContent className="pt-6 space-y-5">
          {/* Prima riga: Anno e Modalità colonne */}
          <div className="flex items-end gap-4 flex-wrap">
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{t("common.year")}</Label>
              <Select value={anno} onValueChange={setAnno}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {anni.map(a => (
                    <SelectItem key={a} value={a}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Vista colonne</Label>
              <div className="flex gap-1 p-1 bg-muted rounded-lg">
                <Button
                  variant={columnMode === "mesi" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setColumnMode("mesi")}
                  className="gap-1.5"
                >
                  <Calendar className="h-4 w-4" />
                  Mesi
                </Button>
                <Button
                  variant={columnMode === "cdc" ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setColumnMode("cdc")}
                  className="gap-1.5"
                >
                  <Building2 className="h-4 w-4" />
                  Centri di Costo
                </Button>
              </div>
            </div>
            
            <Button 
              onClick={fetchReport} 
              disabled={loading || (columnMode === "mesi" ? selectedMesi.length === 0 : selectedCentriCosto.length === 0)}
              className="ml-auto gap-2"
              size="lg"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              Genera Report
            </Button>
            <Button variant="outline" size="lg" onClick={() => setLayoutEditorOpen(true)} className="gap-2" data-testid="configura-margini-btn">
              <Layers className="h-4 w-4" /> Margini
            </Button>
            {reportData && manodoperaData.totale_ore > 0 && (
              <Button
                variant={showCostoOrario ? "default" : "outline"}
                size="lg"
                onClick={() => setShowCostoOrario(!showCostoOrario)}
                className="gap-2"
                data-testid="toggle-costo-orario-btn"
                title="Mostra costo orario €/h"
              >
                €/h {showCostoOrario ? "ON" : "OFF"}
              </Button>
            )}
          </div>
          
          {/* Selezione Mesi */}
          <div className="space-y-3 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg border">
            <div className="flex items-center gap-3 flex-wrap">
              <Label className="text-xs font-bold flex items-center gap-1.5 text-slate-700 dark:text-slate-300">
                <Calendar className="h-3.5 w-3.5 text-indigo-600" />
                {columnMode === "mesi" ? "Colonne (Mesi)" : "Filtro Mesi"}
              </Label>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" onClick={selectAllMesi} className="h-7 text-xs">{t("common.year")}</Button>
                <Button variant="outline" size="sm" onClick={selectH1} className="h-7 text-xs">H1</Button>
                <Button variant="outline" size="sm" onClick={selectH2} className="h-7 text-xs">H2</Button>
                <Button variant="outline" size="sm" onClick={selectQ1} className="h-7 text-xs">Q1</Button>
                <Button variant="outline" size="sm" onClick={selectQ2} className="h-7 text-xs">Q2</Button>
                <Button variant="outline" size="sm" onClick={selectQ3} className="h-7 text-xs">Q3</Button>
                <Button variant="outline" size="sm" onClick={selectQ4} className="h-7 text-xs">Q4</Button>
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {MESI.map(m => (
                <Button
                  key={m.value}
                  variant={selectedMesi.includes(m.value) ? "default" : "outline"}
                  size="sm"
                  onClick={() => toggleMese(m.value)}
                  className={`w-14 h-9 font-medium ${selectedMesi.includes(m.value) ? '' : 'text-muted-foreground'}`}
                >
                  {m.label}
                </Button>
              ))}
            </div>
          </div>
          
          {/* Selezione Centri di Costo */}
          <div className="space-y-3 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg border">
            <div className="flex items-center gap-3 flex-wrap">
              <Label className="text-xs font-bold flex items-center gap-1.5 text-slate-700 dark:text-slate-300">
                <Building2 className="h-3.5 w-3.5 text-violet-600" />
                {columnMode === "cdc" ? "Colonne (Centri di Costo)" : "Filtro Centri di Costo"}
              </Label>
              <Button variant="outline" size="sm" onClick={selectAllCDC} className="h-7 text-xs">{t('common.selectAllItems')}</Button>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {costCenters.length === 0 ? (
                <p className="text-sm text-muted-foreground italic">Nessun centro di costo configurato</p>
              ) : (
                costCenters.map(cc => (
                  <Button
                    key={cc.id}
                    variant={selectedCentriCosto.includes(cc.id) ? "default" : "outline"}
                    size="sm"
                    onClick={() => toggleCentroCosto(cc.id)}
                    className={`min-w-[90px] h-9 font-medium ${selectedCentriCosto.includes(cc.id) ? '' : 'text-muted-foreground'}`}
                  >
                    {cc.codice}
                  </Button>
                ))
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Banner Manodopera (solo se ore presenti e toggle attivo) */}
      {reportData && showCostoOrario && manodoperaData.totale_ore > 0 && (() => {
        const totCosti = columnKeys.reduce((s, k) => s + (totaliCostiAttivi.costi[k] || 0), 0);
        const totRicavi = columnKeys.reduce((s, k) => s + (totaliCostiAttivi.ricavi[k] || 0), 0);
        const risultato = totRicavi - totCosti;
        return (
        <Card className="shadow-sm border-amber-200 bg-amber-50/60 dark:bg-amber-950/20" data-testid="banner-manodopera">
          <CardContent className="py-3">
            <div className="flex items-center justify-between flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-700" />
                <strong>Manodopera nel periodo:</strong>
                <span className="font-mono">{oreTotaliPeriodo.toFixed(1)} h</span>
              </div>
              {totCosti > 0 && oreTotaliPeriodo > 0 && (
                <div>
                  Costo orario medio:&nbsp;
                  <strong className="text-red-700">{(totCosti / oreTotaliPeriodo).toFixed(2)} €/h</strong>
                </div>
              )}
              {totRicavi > 0 && oreTotaliPeriodo > 0 && (
                <div>
                  Ricavo orario medio:&nbsp;
                  <strong className="text-green-700">{(totRicavi / oreTotaliPeriodo).toFixed(2)} €/h</strong>
                </div>
              )}
              {oreTotaliPeriodo > 0 && (
                <div>
                  Margine €/h:&nbsp;
                  <strong className={risultato >= 0 ? "text-blue-700" : "text-red-700"}>
                    {(risultato / oreTotaliPeriodo).toFixed(2)} €/h
                  </strong>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
        );
      })()}

      {/* Tabella Bilancio */}
      {reportData && columns.length > 0 && (
        <Card className="shadow-sm" data-testid="bilancio-report-table">
          <CardHeader className="pb-3 border-b bg-gradient-to-r from-indigo-50/50 to-violet-50/50 dark:from-indigo-950/20 dark:to-violet-950/20">
            <CardTitle className="text-lg flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-indigo-600 to-violet-600 flex items-center justify-center">
                <Euro className="h-4 w-4 text-white" />
              </div>
              <div>
                <span className="font-bold">Bilancio {anno}</span>
                <span className="text-sm font-normal text-muted-foreground ml-3">
                  {columnMode === "mesi" 
                    ? `${selectedMesi.length} mesi • ${selectedCentriCosto.length} CDC` 
                    : `${selectedCentriCosto.length} CDC • ${selectedMesi.length} mesi`}
                </span>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-20">
                  <tr className="border-b bg-slate-50">
                    <th className="text-left p-3 font-semibold min-w-[250px] sticky left-0 bg-slate-50 z-30">Voce</th>
                    {columns.map(col => (
                      <th key={col.key} className="text-right p-3 font-semibold min-w-[100px] bg-slate-50" title={col.label}>
                        {columnMode === "mesi" ? col.label : col.shortLabel}
                      </th>
                    ))}
                    <th className="text-right p-3 font-bold min-w-[120px] bg-slate-100">TOTALE</th>
                  </tr>
                </thead>
                <tbody>
                  {/* VISTA A SCALARE con margini personalizzati */}
                  {vistaScalare && activeLayout ? (
                    <VistaMargini
                      layout={activeLayout}
                      costiPerCategoria={costiPerCategoria}
                      costiOriginaliPerCategoria={costiOriginaliPerCategoria}
                      ricaviPerCategoria={ricaviPerCategoria}
                      columnKeys={columnKeys}
                      formatCurrency={formatCurrency}
                      columnMode={columnMode}
                      costiViewMode={costiViewMode}
                      setCostiViewMode={setCostiViewMode}
                      ripartizioneInfo={ripartizioneInfo}
                      dettaglioMap={dettaglioMapAttivo}
                      columns={columns}
                      onCellClick={openDrilldown}
                      showCostoOrario={showCostoOrario}
                      getOreColonna={getOreColonna}
                      oreTotaliPeriodo={oreTotaliPeriodo}
                    />
                  ) : (
                  <>
                  {/* SEZIONE RICAVI */}
                  <tr className="bg-green-50 border-b-2 border-green-200">
                    <td colSpan={columns.length + 2} className="p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 font-bold text-green-800">
                          <TrendingUp className="h-4 w-4" />
                          RICAVI
                        </div>
                        {Object.keys(ricaviPerCategoria || {}).length > 0 && (
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" onClick={expandAllRicavi} className="h-6 px-2 text-xs">
                              <Maximize2 className="h-3 w-3 mr-1" />Espandi
                            </Button>
                            <Button variant="ghost" size="sm" onClick={collapseAllRicavi} className="h-6 px-2 text-xs">
                              <Minimize2 className="h-3 w-3 mr-1" />Comprimi
                            </Button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                  {Object.keys(ricaviPerCategoria || {}).length === 0 && Object.values(ricavi).length === 0 ? (
                    <tr>
                      <td colSpan={columns.length + 2} className="p-3 text-center text-muted-foreground italic">
                        Nessun ricavo nel periodo
                      </td>
                    </tr>
                  ) : (
                    /* Raggruppa per categoria */
                    Object.entries(ricaviPerCategoria || {})
                      .sort((a, b) => (a[1].ordine || 999) - (b[1].ordine || 999))
                      .map(([catId, catData]) => {
                        const isExpanded = expandedRicaviCategories.has(catId);
                        const catTotale = columnKeys.reduce((sum, k) => sum + (catData.totali[k] || 0), 0);
                        
                        return (
                          <React.Fragment key={`rcat-${catId}`}>
                            {/* Riga categoria (cliccabile) */}
                            <tr 
                              className="bg-green-100/50 cursor-pointer hover:bg-green-100 transition-colors"
                              onClick={() => toggleRicaviCategory(catId)}
                            >
                              <td className="p-2 pl-4 sticky left-0 bg-inherit">
                                <div className="flex items-center gap-2 font-semibold text-green-800">
                                  {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                  <FolderTree className="h-3.5 w-3.5 text-green-600" />
                                  {catData.nome}
                                  <Badge variant="outline" className="ml-2 text-xs font-normal">
                                    {Object.keys(catData.conti).length} conti
                                  </Badge>
                                </div>
                              </td>
                              {columnKeys.map(key => {
                                const ore = getOreColonna(key);
                                const v = catData.totali[key] || 0;
                                return (
                                  <td key={key} className="p-2 text-right font-mono text-green-700 font-medium">
                                    <div>{v ? formatCurrency(v) : "-"}</div>
                                    {showCostoOrario && ore > 0 && v > 0 && (
                                      <div className="text-[11px] font-medium text-green-500">{(v/ore).toFixed(2)} €/h</div>
                                    )}
                                  </td>
                                );
                              })}
                              <td className="p-2 text-right font-mono font-bold bg-green-100/50 text-green-800">
                                <div>{formatCurrency(catTotale)}</div>
                                {showCostoOrario && oreTotaliPeriodo > 0 && (
                                  <div className="text-[11px] font-medium text-green-600">{(catTotale/oreTotaliPeriodo).toFixed(2)} €/h</div>
                                )}
                              </td>
                            </tr>
                            {/* Righe dettaglio conti (se espanso) */}
                            {isExpanded && Object.values(catData.conti).map((conto, idx) => (
                              <tr key={`rc-${catId}-${idx}`} className={idx % 2 === 0 ? "bg-white" : "bg-green-50/30"}>
                                <td className="p-2 pl-12 sticky left-0 bg-inherit text-sm">
                                  {conto.codice && <span className="text-muted-foreground mr-2">{conto.codice}</span>}
                                  {conto.nome}
                                </td>
                                {columnKeys.map(key => {
                                  const ore = getOreColonna(key);
                                  const v = conto.values[key];
                                  return (
                                    <td key={key} className={`p-2 text-right font-mono text-green-600 text-sm ${v ? "cursor-pointer hover:bg-green-100 hover:underline transition-colors" : ""}`}
                                      onClick={() => { if (v) openDrilldown(conto.nome, conto.codice, key); }}
                                      data-testid={`cell-ricavo-${conto.codice}-${key}`}
                                    >
                                      <div>{v ? formatCurrency(v) : "-"}</div>
                                      {showCostoOrario && ore > 0 && v > 0 && (
                                        <div className="text-[11px] font-medium text-green-400">{(v/ore).toFixed(2)} €/h</div>
                                      )}
                                    </td>
                                  );
                                })}
                                <td className="p-2 text-right font-mono bg-green-50 text-green-700 text-sm">
                                  {(() => {
                                    const tot = getRowTotal(conto.values, columnKeys);
                                    return (
                                      <>
                                        <div>{formatCurrency(tot)}</div>
                                        {showCostoOrario && oreTotaliPeriodo > 0 && tot > 0 && (
                                          <div className="text-[11px] font-medium text-green-500">{(tot/oreTotaliPeriodo).toFixed(2)} €/h</div>
                                        )}
                                      </>
                                    );
                                  })()}
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      })
                  )}
                  {/* Totale Ricavi */}
                  <tr className="border-t-2 border-green-300 bg-green-100 font-bold">
                    <td className="p-3 sticky left-0 bg-green-100">Totale Ricavi</td>
                    {columnKeys.map(key => {
                      const ore = getOreColonna(key);
                      const val = totali.ricavi[key] || 0;
                      return (
                        <td key={key} className="p-3 text-right font-mono text-green-800">
                          <div>{formatCurrency(val)}</div>
                          {showCostoOrario && ore > 0 && (
                            <div className="text-xs font-semibold text-green-600">{(val/ore).toFixed(2)} €/h</div>
                          )}
                        </td>
                      );
                    })}
                    <td className="p-3 text-right font-mono text-lg bg-green-200 text-green-900">
                      <div>{formatCurrency(columnKeys.reduce((sum, k) => sum + (totali.ricavi[k] || 0), 0))}</div>
                      {showCostoOrario && oreTotaliPeriodo > 0 && (
                        <div className="text-xs font-semibold text-green-700">
                          {(columnKeys.reduce((sum, k) => sum + (totali.ricavi[k] || 0), 0) / oreTotaliPeriodo).toFixed(2)} €/h
                        </div>
                      )}
                    </td>
                  </tr>

                  {/* Spacer */}
                  <tr className="h-4 bg-white" />

                  {/* SEZIONE COSTI con toggle Originali/Ripartiti */}
                  <tr className="bg-red-50 border-b-2 border-red-200">
                    <td colSpan={columns.length + 2} className="p-3">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-2 font-bold text-red-800">
                          <TrendingDown className="h-4 w-4" />
                          COSTI
                        </div>
                        <div className="flex items-center gap-2">
                          {columnMode === "cdc" && (
                            <div className="flex gap-0.5 p-0.5 bg-red-100 rounded-md" data-testid="costi-view-toggle">
                              <button
                                onClick={() => { setCostiViewMode("originali"); setExpandedContiRows(new Set()); }}
                                className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${costiViewMode === "originali" ? "bg-white text-red-800 shadow-sm" : "text-red-600 hover:text-red-800"}`}
                                data-testid="toggle-costi-originali"
                              >
                                Costi Originali
                              </button>
                              <button
                                onClick={() => setCostiViewMode("ripartiti")}
                                className={`px-2.5 py-1 text-xs font-medium rounded transition-colors ${costiViewMode === "ripartiti" ? "bg-white text-red-800 shadow-sm" : "text-red-600 hover:text-red-800"}`}
                                data-testid="toggle-costi-ripartiti"
                              >
                                Costi Ripartiti
                              </button>
                            </div>
                          )}
                          {Object.keys(costiCategorieAttive || {}).length > 0 && (
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="sm" onClick={expandAllCosti} className="h-6 px-2 text-xs">
                                <Maximize2 className="h-3 w-3 mr-1" />Espandi
                              </Button>
                              <Button variant="ghost" size="sm" onClick={collapseAllCosti} className="h-6 px-2 text-xs">
                                <Minimize2 className="h-3 w-3 mr-1" />Comprimi
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                      {costiViewMode === "ripartiti" && columnMode === "cdc" && (
                        <p className="text-[10px] text-red-600/70 mt-1 flex items-center gap-1">
                          <Info className="h-3 w-3" />
                          I costi dei CDC "da ripartire" sono redistribuiti sui CDC destinatari. Passa a "Costi Originali" per vedere l'allocazione prima della ripartizione.
                        </p>
                      )}
                      {costiViewMode === "originali" && columnMode === "cdc" && (
                        <p className="text-[10px] text-blue-600/70 mt-1 flex items-center gap-1">
                          <Info className="h-3 w-3" />
                          Vista pre-ripartizione: i costi sono sul centro di costo originale, prima della redistribuzione.
                        </p>
                      )}
                    </td>
                  </tr>
                  {Object.keys(costiCategorieAttive || {}).length === 0 && Object.values(costi).length === 0 ? (
                    <tr>
                      <td colSpan={columns.length + 2} className="p-3 text-center text-muted-foreground italic">
                        Nessun costo nel periodo
                      </td>
                    </tr>
                  ) : (
                    Object.entries(costiCategorieAttive || {})
                      .sort((a, b) => (a[1].ordine || 999) - (b[1].ordine || 999))
                      .map(([catId, catData]) => {
                        const isExpanded = expandedCostiCategories.has(catId);
                        const catTotale = columnKeys.reduce((sum, k) => sum + (catData.totali[k] || 0), 0);
                        
                        return (
                          <React.Fragment key={`ccat-${catId}`}>
                            <tr 
                              className="bg-red-100/50 cursor-pointer hover:bg-red-100 transition-colors"
                              onClick={() => toggleCostiCategory(catId)}
                            >
                              <td className="p-2 pl-4 sticky left-0 bg-inherit">
                                <div className="flex items-center gap-2 font-semibold text-red-800">
                                  {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                  <FolderTree className="h-3.5 w-3.5 text-red-600" />
                                  {catData.nome}
                                  <Badge variant="outline" className="ml-2 text-xs font-normal">
                                    {Object.keys(catData.conti).length} conti
                                  </Badge>
                                </div>
                              </td>
                              {columnKeys.map(key => {
                                const ore = getOreColonna(key);
                                const v = catData.totali[key] || 0;
                                return (
                                  <td key={key} className="p-2 text-right font-mono text-red-700 font-medium">
                                    <div>{v ? formatCurrency(v) : "-"}</div>
                                    {showCostoOrario && ore > 0 && v > 0 && (
                                      <div className="text-[11px] font-medium text-red-500">{(v/ore).toFixed(2)} €/h</div>
                                    )}
                                  </td>
                                );
                              })}
                              <td className="p-2 text-right font-mono font-bold bg-red-100/50 text-red-800">
                                <div>{formatCurrency(catTotale)}</div>
                                {showCostoOrario && oreTotaliPeriodo > 0 && (
                                  <div className="text-[11px] font-medium text-red-600">{(catTotale/oreTotaliPeriodo).toFixed(2)} €/h</div>
                                )}
                              </td>
                            </tr>
                            {isExpanded && Object.values(catData.conti).map((conto, idx) => {
                              const contoKey = conto.nome;
                              const hasRipartizione = columnMode === "cdc" && costiViewMode === "ripartiti" && ripartizioneInfo && ripartizioneInfo[contoKey];
                              const isContoExpanded = expandedContiRows.has(contoKey);
                              const contoRowTotal = getRowTotal(conto.values, columnKeys);
                              
                              return (
                                <React.Fragment key={`cc-${catId}-${idx}`}>
                                  <tr 
                                    className={`${idx % 2 === 0 ? "bg-white" : "bg-red-50/30"} ${hasRipartizione ? "cursor-pointer hover:bg-red-50 transition-colors" : ""}`}
                                    onClick={() => hasRipartizione && toggleContoRipartizione(contoKey)}
                                    data-testid={`conto-row-${conto.codice || idx}`}
                                  >
                                    <td className="p-2 pl-12 sticky left-0 bg-inherit text-sm">
                                      <div className="flex items-center gap-1.5">
                                        {hasRipartizione && (
                                          isContoExpanded 
                                            ? <ChevronDown className="h-3.5 w-3.5 text-red-400 shrink-0" /> 
                                            : <ChevronRight className="h-3.5 w-3.5 text-red-400 shrink-0" />
                                        )}
                                        {conto.codice && <span className="text-muted-foreground">{conto.codice}</span>}
                                        <span>{conto.nome}</span>
                                        {hasRipartizione && (
                                          <ArrowLeftRight className="h-3 w-3 text-amber-500 shrink-0 ml-1" title="Contiene costi ripartiti" />
                                        )}
                                      </div>
                                    </td>
                                    {columnKeys.map(key => {
                                      const ore = getOreColonna(key);
                                      const v = conto.values[key];
                                      return (
                                        <td key={key} className={`p-2 text-right font-mono text-red-600 text-sm ${v ? "cursor-pointer hover:bg-red-100 hover:underline transition-colors" : ""}`}
                                          onClick={(e) => { e.stopPropagation(); if (v) openDrilldown(conto.nome, conto.codice, key); }}
                                          data-testid={`cell-costo-${conto.codice}-${key}`}
                                        >
                                          <div>{v ? formatCurrency(v) : "-"}</div>
                                          {showCostoOrario && ore > 0 && v > 0 && (
                                            <div className="text-[11px] font-medium text-red-400">{(v/ore).toFixed(2)} €/h</div>
                                          )}
                                        </td>
                                      );
                                    })}
                                    <td className="p-2 text-right font-mono bg-red-50 text-red-700 text-sm">
                                      <div>{formatCurrency(contoRowTotal)}</div>
                                      {showCostoOrario && oreTotaliPeriodo > 0 && contoRowTotal > 0 && (
                                        <div className="text-[11px] font-medium text-red-500">{(contoRowTotal/oreTotaliPeriodo).toFixed(2)} €/h</div>
                                      )}
                                    </td>
                                  </tr>
                                  {/* Sub-rows: Costi Diretti e Costi Ripartiti */}
                                  {hasRipartizione && isContoExpanded && (
                                    <>
                                      <tr className="bg-blue-50/40" data-testid={`conto-diretti-${conto.codice || idx}`}>
                                        <td className="p-1.5 pl-16 sticky left-0 bg-blue-50/40 text-xs text-blue-700 font-medium">
                                          di cui Diretti
                                        </td>
                                        {columnKeys.map(key => {
                                          const info = ripartizioneInfo[contoKey]?.[key];
                                          return (
                                            <td key={key} className="p-1.5 text-right font-mono text-blue-600 text-xs">
                                              {info?.diretta ? formatCurrency(info.diretta) : "-"}
                                            </td>
                                          );
                                        })}
                                        <td className="p-1.5 text-right font-mono bg-blue-50/60 text-blue-700 text-xs font-medium">
                                          {formatCurrency(columnKeys.reduce((sum, k) => sum + (ripartizioneInfo[contoKey]?.[k]?.diretta || 0), 0))}
                                        </td>
                                      </tr>
                                      <tr className="bg-amber-50/40" data-testid={`conto-ripartiti-${conto.codice || idx}`}>
                                        <td className="p-1.5 pl-16 sticky left-0 bg-amber-50/40 text-xs text-amber-700 font-medium">
                                          di cui Ripartiti
                                        </td>
                                        {columnKeys.map(key => {
                                          const info = ripartizioneInfo[contoKey]?.[key];
                                          return (
                                            <td key={key} className="p-1.5 text-right font-mono text-amber-600 text-xs">
                                              {info?.ripartizione ? formatCurrency(info.ripartizione) : "-"}
                                            </td>
                                          );
                                        })}
                                        <td className="p-1.5 text-right font-mono bg-amber-50/60 text-amber-700 text-xs font-medium">
                                          {formatCurrency(columnKeys.reduce((sum, k) => sum + (ripartizioneInfo[contoKey]?.[k]?.ripartizione || 0), 0))}
                                        </td>
                                      </tr>
                                      {/* Dettaglio provenienza ripartizioni */}
                                      {columnKeys.some(k => ripartizioneInfo[contoKey]?.[k]?.dettaglio?.length > 0) && (
                                        (() => {
                                          // Aggrega dettaglio ripartizioni da tutti i mesi
                                          const dettaglioAggregato = {};
                                          columnKeys.forEach(k => {
                                            (ripartizioneInfo[contoKey]?.[k]?.dettaglio || []).forEach(d => {
                                              const dKey = d.da_cdc_nome;
                                              if (!dettaglioAggregato[dKey]) {
                                                dettaglioAggregato[dKey] = { nome: d.da_cdc_nome, percentuale: d.percentuale, perMese: {} };
                                              }
                                              dettaglioAggregato[dKey].perMese[k] = (dettaglioAggregato[dKey].perMese[k] || 0) + d.importo;
                                            });
                                          });
                                          return Object.values(dettaglioAggregato).map((det, dIdx) => (
                                            <tr key={`det-${contoKey}-${dIdx}`} className="bg-amber-50/20">
                                              <td className="p-1 pl-20 sticky left-0 bg-amber-50/20 text-[10px] text-amber-600 italic">
                                                da {det.nome} ({det.percentuale}%)
                                              </td>
                                              {columnKeys.map(key => (
                                                <td key={key} className="p-1 text-right font-mono text-amber-500 text-[10px]">
                                                  {det.perMese[key] ? formatCurrency(det.perMese[key]) : "-"}
                                                </td>
                                              ))}
                                              <td className="p-1 text-right font-mono bg-amber-50/30 text-amber-600 text-[10px]">
                                                {formatCurrency(columnKeys.reduce((s, k) => s + (det.perMese[k] || 0), 0))}
                                              </td>
                                            </tr>
                                          ));
                                        })()
                                      )}
                                    </>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </React.Fragment>
                        );
                      })
                  )}
                  {/* Totale Costi */}
                  <tr className="border-t-2 border-red-300 bg-red-100 font-bold">
                    <td className="p-3 sticky left-0 bg-red-100">
                      Totale Costi {costiViewMode === "originali" && columnMode === "cdc" ? "(Originali)" : ""}
                    </td>
                    {columnKeys.map(key => {
                      const ore = getOreColonna(key);
                      const val = totaliCostiAttivi.costi[key] || 0;
                      return (
                        <td key={key} className="p-3 text-right font-mono text-red-800">
                          <div>{formatCurrency(val)}</div>
                          {showCostoOrario && ore > 0 && (
                            <div className="text-xs font-semibold text-red-600">{(val/ore).toFixed(2)} €/h</div>
                          )}
                        </td>
                      );
                    })}
                    <td className="p-3 text-right font-mono text-lg bg-red-200 text-red-900">
                      <div>{formatCurrency(columnKeys.reduce((sum, k) => sum + (totaliCostiAttivi.costi[k] || 0), 0))}</div>
                      {showCostoOrario && oreTotaliPeriodo > 0 && (
                        <div className="text-xs font-semibold text-red-700">
                          {(columnKeys.reduce((sum, k) => sum + (totaliCostiAttivi.costi[k] || 0), 0) / oreTotaliPeriodo).toFixed(2)} €/h
                        </div>
                      )}
                    </td>
                  </tr>

                  {/* Spacer */}
                  <tr className="h-4 bg-white" />

                  {/* RISULTATO */}
                  <tr className="bg-blue-100 border-t-2 border-blue-300 font-bold text-lg">
                    <td className="p-4 sticky left-0 bg-blue-100">
                      <div className="flex items-center gap-2">
                        <Euro className="h-5 w-5 text-blue-700" />
                        RISULTATO
                      </div>
                    </td>
                    {columnKeys.map(key => {
                      const risultato = (totaliCostiAttivi.ricavi[key] || 0) - (totaliCostiAttivi.costi[key] || 0);
                      const ore = getOreColonna(key);
                      return (
                        <td key={key} className={`p-4 text-right font-mono ${risultato >= 0 ? "text-blue-800" : "text-orange-700"}`}>
                          <div>{formatCurrency(risultato)}</div>
                          {showCostoOrario && ore > 0 && (
                            <div className={`text-xs font-semibold ${risultato >= 0 ? "text-blue-600" : "text-orange-600"}`}>
                              {(risultato/ore).toFixed(2)} €/h
                            </div>
                          )}
                        </td>
                      );
                    })}
                    {(() => {
                      const totaleRisultato = columnKeys.reduce((sum, k) => sum + ((totaliCostiAttivi.ricavi[k] || 0) - (totaliCostiAttivi.costi[k] || 0)), 0);
                      return (
                        <td className={`p-4 text-right font-mono text-xl ${totaleRisultato >= 0 ? "bg-blue-200 text-blue-900" : "bg-orange-200 text-orange-900"}`}>
                          <div>{formatCurrency(totaleRisultato)}</div>
                          {showCostoOrario && oreTotaliPeriodo > 0 && (
                            <div className={`text-xs font-semibold ${totaleRisultato >= 0 ? "text-blue-700" : "text-orange-700"}`}>
                              {(totaleRisultato/oreTotaliPeriodo).toFixed(2)} €/h
                            </div>
                          )}
                        </td>
                      );
                    })()}
                  </tr>
                  </>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Riepilogo per Centro di Costo con toggle Ripartizione */}
      {reportData && cdcRiepilogoData && columnMode === "mesi" && (
        <CdcRiepilogoCard
          cdcRiepilogoData={cdcRiepilogoData}
          selectedMesi={selectedMesi}
          formatCurrency={formatCurrency}
        />
      )}

      {/* Placeholder iniziale */}
      {!reportData && !loading && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Euro className="h-12 w-12 mx-auto mb-4 opacity-30" />
            <p>Seleziona l'anno, la modalità colonne (Mesi o Centri di Costo), poi clicca su "Genera Report"</p>
            <p className="text-sm mt-2">
              <strong>Modalità Mesi:</strong> Visualizza i mesi come colonne, filtra per centri di costo<br/>
              <strong>Modalità CDC:</strong> Visualizza i centri di costo come colonne, filtra per mesi
            </p>
          </CardContent>
        </Card>
      )}

      {/* Layout Editor Dialog */}
      <LayoutEditorDialog
        open={layoutEditorOpen}
        onClose={() => setLayoutEditorOpen(false)}
        contiDisponibili={contiDisponibili}
        categorieDisponibili={categorieDisponibili}
        onSaved={fetchActiveLayout}
      />

      {/* Drill-down Dialog */}
      <DrilldownDialog
        open={drilldown.open}
        onClose={() => setDrilldown({ ...drilldown, open: false })}
        entries={drilldown.entries}
        contoNome={drilldown.contoNome}
        contoCodice={drilldown.contoCodice}
        columnLabel={drilldown.columnLabel}
        formatCurrency={formatCurrency}
      />
    </div>
  );
}
